import {
    Body,
    Brand,
    ContractDetails,
    DeliveryDates,
    Equipment,
    Garage,
    GarageChain,
    Gearbox,
    Generation,
    Key,
    Motorization,
    NumberPlate,
    Organization,
    PhoneInput,
    Place,
    PremiumService,
    Service,
    Slot,
    User
} from "@App/types";
import {Action} from "@App/state/actions";
import {ActionType} from "@App/state/action-types";

declare global {
    var organization: Organization
    interface Window {
        dataLayer: any[];
    }
}

const getOrganization = (): Organization => {
    return window.organization ?? {
        id: 1,
        uuid: '6992a5a7-219e-43d8-be0f-a00e4eda5be0',
        name: 'repareo',
    }
}

interface OrderReducerState {
    organization?: Organization
    service?: Service
    place?: Place
    garage?: Garage
    premiumServices?: PremiumService[]
    firstName?: string
    lastName?: string
    email?: string
    phone?: PhoneInput
    user?: User
    slot?: Slot
    brand?: Brand
    generation?: Generation
    motorization?: Motorization
    key?: Key
    body?: Body
    gearbox?: Gearbox
    mileage?: number
    initialRegistrationDate?: Date
    equipments?: Equipment[]
    subRepairs?: string[]
    // vehicle delivery
    contractDetails?: ContractDetails,
    deliveryDates?: DeliveryDates,
    garage_chain?: GarageChain[],
    salutation?: number,
    numberPlate?: NumberPlate,
    rentalCarSelection?: string,
}

const initialState: OrderReducerState = {
    organization: getOrganization(),
    service: null,
    // vehicle
    brand: null,
    generation: null,
    motorization: null,
    key: {
        hsn: '',
        tsn: ''
    },
    body: null,
    gearbox: null,
    mileage: null,
    initialRegistrationDate: null,
    equipments: [],
    // place
    place: null,
    garage: null,
    premiumServices: [],
    firstName: '',
    lastName: '',
    email: '',
    rentalCarSelection: '',
    phone: {
        country: '+49',
        number: null,
    },
    user: null,
    slot: null,
    subRepairs: [],
    // vehicle delivery
    contractDetails: {
        carId: null,
        agreementNumber: null,
        name: null,
        street: null,
        postcode: null,
        city: null,
        phone: null,
    },
    deliveryDates: {
        date1: null,
        date2: null,
        date3: null,
    },
    garage_chain: null,
    salutation: 0,
    numberPlate: {
        region: '',
        firstID: '',
        secondID: ''
    }
}

const orderReducer = (state: OrderReducerState = initialState, action: Action) => {
    switch (action.type) {
        // organization actions
        case ActionType.CHOOSE_ORGANIZATION:
            return {
                ...state,
                organization: action.payload
            }
        // service actions
        case ActionType.CHOOSE_SERVICE:
            return {
                ...state,
                service: action.payload
            }
        case ActionType.REMOVE_SERVICE:
            return {
                ...state,
                service: null
            }
        // brand actions
        case ActionType.CHOOSE_BRAND:
            return {
                ...state,
                brand: action.payload
            }
        // generation actions
        case ActionType.CHOOSE_GENERATION:
            return {
                ...state,
                generation: action.payload
            }
        // motorization actions
        case ActionType.CHOOSE_MOTORIZATION:
            return {
                ...state,
                motorization: action.payload
            }
        case ActionType.SET_KEY:
            return {
                ...state,
                key: action.payload
            }
        // body actions
        case ActionType.CHOOSE_BODY:
            return {
                ...state,
                body: action.payload
            }
        // gearbox actions
        case ActionType.CHOOSE_GEARBOX:
            return {
                ...state,
                gearbox: action.payload
            }
        // mileage actions
        case ActionType.SET_MILEAGE:
            return {
                ...state,
                mileage: action.payload
            }
        // initial registration date actions
        case ActionType.SET_REGISTRATION_DATE:
            return {
                ...state,
                initialRegistrationDate: action.payload
            }
        // equipment actions
        case ActionType.ADD_EQUIPMENT:
            return {
                ...state,
                equipments: [...state.equipments, action.payload]
            }
        case ActionType.REMOVE_EQUIPMENT:
            return {
                ...state,
                equipments: state.equipments.filter((equipment) => equipment.id !== action.payload.id)
            }
        // place actions
        case ActionType.CHOOSE_PLACE:
            return {
                ...state,
                place: action.payload
            }
        // garage actions
        case ActionType.CHOOSE_GARAGE:
            return {
                ...state,
                garage: action.payload
            }
        case ActionType.REMOVE_GARAGE:
            return {
                ...state,
                garage: null
            }
        case ActionType.CHOOSE_SLOT:
            return {
                ...state,
                slot: action.payload
            }
        case ActionType.ADD_PREMIUM_SERVICE:
            return state.premiumServices.some((p => p.id === action.payload.id)) ? {...state} : {...state,  premiumServices: [...state.premiumServices, action.payload]}

        case ActionType.REMOVE_PREMIUM_SERVICE:
            return {
                ...state,
                premiumServices: state.premiumServices.filter((premiumService) => premiumService.id !== action.payload.id)
            }
        case ActionType.CLEAR_PREMIUM_SERVICES:
            return {
                ...state,
                premiumServices: action.payload
            }
        case ActionType.SET_SALUTATION:
            return {
                ...state,
                salutation: action.payload
            }
        case ActionType.SET_FIRST_NAME:
            return {
                ...state,
                firstName: action.payload
            }
        case ActionType.SET_LAST_NAME:
            return {
                ...state,
                lastName: action.payload
            }
        case ActionType.SET_EMAIL:
            return {
                ...state,
                email: action.payload
            }
        case ActionType.SET_PHONE:
            return {
                ...state,
                phone: action.payload
            }
        case ActionType.SET_NUMBERPLATE:
            return {
                ...state,
                numberPlate: action.payload
            }
        // subrepair actions
        case ActionType.ADD_SUB_REPAIR:
            return {
                ...state,
                subRepairs: [...state.subRepairs, action.payload]
            }
        case ActionType.REMOVE_SUB_REPAIR:
            return {
                ...state,
                subRepairs: state.subRepairs.filter((subRepair) => subRepair !== action.payload)
            }
        // vehicle delivery actions
        case ActionType.SET_CONTRACT_DETAILS:
            return {
                ...state,
                contractDetails: action.payload
            }
        case ActionType.UPDATE_CONTRACT_DETAILS:
            return {
                ...state,
                contractDetails: action.payload
            }
        case ActionType.SET_DELIVERY_DATES:
            return {
                ...state,
                deliveryDates: action.payload
            }
        case ActionType.SET_GARAGE_CHAINS:
            return {
                ...state,
                garage_chain: action.payload
            }
        case ActionType.SET_RENTAL_CAR_SELECTION:
            return {
                ...state,
                rentalCarSelection: action.payload
            }
    }
    return state
}

export default orderReducer
